import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { kitsAPI } from '../../utils/api';
import { useCartStore } from '../../store';
import { LoadingSpinner, EmptyState } from '../../components/common';
import QtyStepper from '../../components/common/QtyStepper';

const KitPage = () => {
  const { schoolId, grade, kitId } = useParams();
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({}); // product_id -> qty
  const [selectedColors, setSelectedColors] = useState({}); // product_id -> selected color
  const [added, setAdded] = useState(false);
  const { addKit, addFullKit } = useCartStore();
  const [fullKitAdded, setFullKitAdded] = useState(false);
  const [errorAlert, setErrorAlert] = useState('');
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    const fetch = kitId
      ? kitsAPI.getById(kitId)
      : kitsAPI.getBySchoolGrade(schoolId, grade);

    fetch
      .then(r => {
        setKit(r.data);
        const qtyMap = {};
        const colorMap = {};
        r.data.items?.forEach(item => { 
          qtyMap[item.product_id] = item.default_qty; 
          if (item.colors) {
            colorMap[item.product_id] = item.colors.split(',').map(c => c.trim()).filter(Boolean);
          }
        });
        setQuantities(qtyMap);
        setSelectedColors(colorMap);
        setErrorAlert('');
        setHighlightedItem(null);
      })
      .catch(() => setKit(null))
      .finally(() => setLoading(false));
  }, [schoolId, grade, kitId]);

  if (loading) return <LoadingSpinner />;
  if (!kit) return (
    <div className="container">
      <EmptyState icon="😕" title="Kit Not Found" message="No kit is available for this school and grade yet." />
      <div className="text-center" style={{ marginTop: '1rem' }}>
        <button className="btn btn-outline" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    </div>
  );

  // Real-time price calculation
  const activeItems = kit?.items?.filter(item => (quantities[item.product_id] ?? 0) > 0) || [];
  const total = activeItems.reduce((sum, item) => sum + item.price * (quantities[item.product_id] || 0), 0);
  const hasMissingColors = activeItems.some(item => {
    const rawColors = item.colors || item.product_colors || '';
    const itemColors = rawColors ? rawColors.split(',').map(c => c.trim()).filter(Boolean) : [];
    const chosenColors = selectedColors[item.product_id] || [];
    return itemColors.length > 0 && chosenColors.length === 0;
  });

  const handleQtyChange = (productId, qty) => {
    setQuantities(prev => ({ ...prev, [productId]: qty }));
  };

  const handleAddToCart = () => {
    const missingColorItem = activeItems.find(item => {
      const rawColors = item.colors || item.product_colors || '';
      const itemColors = rawColors ? rawColors.split(',').map(c => c.trim()).filter(Boolean) : [];
      const chosenColors = selectedColors[item.product_id] || [];
      return itemColors.length > 0 && chosenColors.length === 0;
    });

    if (missingColorItem) {
      setErrorAlert(`Please select a color for "${missingColorItem.name}" before adding to cart.`);
      setHighlightedItem(missingColorItem.product_id);
      const el = document.getElementById(`kit-item-${missingColorItem.product_id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setHighlightedItem(null);

    addKit(kit, quantities, selectedColors);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const hasMissingColorsFullKit = kit?.items?.some(item => {
    const rawColors = item.colors || item.product_colors || '';
    const itemColors = rawColors ? rawColors.split(',').map(c => c.trim()).filter(Boolean) : [];
    const chosenColors = selectedColors[item.product_id] || [];
    return itemColors.length > 0 && chosenColors.length === 0;
  });

  const handleBuyFullKit = () => {
    const missingColorItem = kit?.items?.find(item => {
      const rawColors = item.colors || item.product_colors || '';
      const itemColors = rawColors ? rawColors.split(',').map(c => c.trim()).filter(Boolean) : [];
      const chosenColors = selectedColors[item.product_id] || [];
      return itemColors.length > 0 && chosenColors.length === 0;
    });

    if (missingColorItem) {
      setErrorAlert(`Please select a color for "${missingColorItem.name}" before buying the full kit.`);
      setHighlightedItem(missingColorItem.product_id);
      const el = document.getElementById(`kit-item-${missingColorItem.product_id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setHighlightedItem(null);
    addFullKit(kit, selectedColors);
    setFullKitAdded(true);
    setTimeout(() => setFullKitAdded(false), 2000);
  };

  return (
    <div className="container" style={{ padding: '1.5rem 1rem' }}>
      <button className="btn btn-outline btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: '1rem' }}>← Back</button>

      {/* Kit Header */}
      <div className="card" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '2.5rem' }}>🎒</div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800 }}>{kit.name}</h1>
          <div style={{ color: '#6b7280', fontSize: '0.88rem' }}>
            {kit.school_name} • Grade {kit.grade}
            {kit.gender !== 'all' && ` • ${kit.gender}`}
          </div>
          {kit.description && <p style={{ marginTop: '0.3rem', fontSize: '0.88rem' }}>{kit.description}</p>}
        </div>
      </div>

      {/* Buy Full Kit Option */}
      {kit.full_kit_price && (
        <div style={{
          marginBottom: '1rem', borderRadius: 12, padding: '1rem 1.25rem',
          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
          border: '2px solid #2563eb', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem',
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e3a8a' }}>🏷️ Buy Complete Kit — Save more and get free shipping!</div>
            <div style={{ fontSize: '0.83rem', color: '#1d4ed8', marginTop: '0.2rem' }}>
              Get all {kit.items?.length} items as a ready-to-go bundle at a special price.
            </div>
            {total > parseFloat(kit.full_kit_price) && (
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.15rem' }}>
                Individual total: <span style={{ textDecoration: 'line-through' }}>${total.toFixed(2)}</span>
                &nbsp;— you save <span style={{ color: '#059669', fontWeight: 700 }}>${(total - parseFloat(kit.full_kit_price)).toFixed(2)}</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Bundle price</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#2563eb', lineHeight: 1, whiteSpace: 'nowrap' }}>${parseFloat(kit.full_kit_price).toFixed(2)}</div>
            </div>
            <button
              className={`btn btn-lg ${fullKitAdded ? 'btn-secondary' : 'btn-primary'}`}
              onClick={handleBuyFullKit}
              style={{ whiteSpace: 'nowrap' }}
            >
              {fullKitAdded ? '✓ Added!' : '🛒 Buy Full Kit'}
            </button>
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '1rem' }}>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: '1rem' }}>Kit Items</h2>
          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>You can customize the Kit items below:</span>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {kit.items?.map(item => {
                const qty = quantities[item.product_id] ?? 0;
                const subtotal = item.price * qty;
                const removed = qty === 0;
                const rawColors = item.colors || item.product_colors || '';
                const itemColors = rawColors
                  ? rawColors.split(',').map(c => c.trim()).filter(Boolean)
                  : [];
                const chosenColors = selectedColors[item.product_id] || [];
                return (
                  <React.Fragment key={item.product_id}>
                    <tr id={`kit-item-${item.product_id}`} style={{ opacity: removed ? 0.35 : 1, transition: 'all 0.3s', backgroundColor: highlightedItem === item.product_id ? '#fef2f2' : 'transparent', boxShadow: highlightedItem === item.product_id ? 'inset 0 0 0 2px #ef4444' : 'none' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          {(item.image_data || item.image_url) ? (
                            <img
                              src={item.image_data || item.image_url}
                              alt={item.name}
                              onClick={() => setZoomedImage(item.image_data || item.image_url)}
                              style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0, cursor: 'zoom-in' }}
                            />
                          ) : (
                            <div style={{ width: 48, height: 48, borderRadius: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>📦</div>
                          )}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{item.name}</div>

                          </div>
                        </div>
                      </td>

                      <td>
                        <QtyStepper
                          value={qty}
                          onChange={v => handleQtyChange(item.product_id, v)}
                          min={0}
                          max={item.stock_qty}
                        />
                      </td>
                      <td style={{ fontWeight: 700, color: removed ? '#9ca3af' : '#2563eb' }}>
                        {removed ? <span style={{ fontSize: '0.78rem' }}>Removed</span> : `$${subtotal.toFixed(2)}`}
                      </td>
                    </tr>
                    {itemColors.length > 0 && !removed && (
                      <tr>
                        <td colSpan={3} style={{ padding: '0.3rem 0.75rem 0.6rem 3.2rem', background: '#f8fafc', borderTop: 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>Choose color:</span>
                            {itemColors.map(c => (
                              <button
                                key={c}
                                onClick={() => {
                                  const current = selectedColors[item.product_id] || [];
                                  const next = current.includes(c)
                                    ? current.filter(x => x !== c)
                                    : [...current, c];
                                  setSelectedColors({ ...selectedColors, [item.product_id]: next });
                                }}
                                style={{
                                  borderRadius: 999, padding: '0.15rem 0.6rem',
                                  fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                  border: chosenColors.includes(c) ? '2px solid #2563eb' : '2px solid #e5e7eb',
                                  background: chosenColors.includes(c) ? '#dbeafe' : '#fff',
                                  color: chosenColors.includes(c) ? '#1e40af' : '#374151',
                                  transition: 'all 0.15s',
                                }}
                              >{c}</button>
                            ))}
                            {chosenColors.length === 0 && (
                              <span style={{ fontSize: '0.68rem', color: '#f59e0b', fontWeight: 600, marginLeft: '0.25rem' }}>↑ Select color(s)</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary & CTA */}
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>{activeItems.length} of {kit.items?.length} items selected</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 900 }}>Total: ${total.toFixed(2)}</div>
        </div>
        <div className="flex gap-3">
          <button
            className={`btn ${added ? 'btn-secondary' : 'btn-primary'} btn-lg`}
            onClick={handleAddToCart}
            disabled={activeItems.length === 0}
          >
            {added ? '✓ Added to Cart!' : '🛒 Add items to cart'}
          </button>
        </div>
      </div>

      {/* Custom Error Modal */}
      {errorAlert && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={() => setErrorAlert('')}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '2rem', maxWidth: 400, width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', textAlign: 'center', animation: 'popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', color: '#ef4444', fontSize: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
              ⚠️
            </div>
            <h3 style={{ margin: '0 0 0.75rem', color: '#111827', fontSize: '1.25rem', fontWeight: 800 }}>Action Required</h3>
            <p style={{ color: '#4b5563', fontSize: '0.95rem', lineHeight: 1.5, marginBottom: '1.5rem' }}>
              {errorAlert}
            </p>
            <button className="btn btn-primary w-full" onClick={() => setErrorAlert('')} style={{ padding: '0.75rem', fontSize: '1rem' }}>
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Zoom Modal */}
      {zoomedImage && (
        <div 
          style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', cursor: 'zoom-out' }} 
          onClick={() => setZoomedImage(null)}
        >
          <img src={zoomedImage} alt="zoomed" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 8, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', animation: 'popIn 0.2s ease-out' }} />
        </div>
      )}
    </div>
  );
};

const styles = {};

export default KitPage;
